import { Button, Col, Form, InputNumber, Row } from "antd";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import * as React from "react";
import { useTranslation } from "react-i18next";

interface ModelPositionModalProps extends ModalComponentProps {
  fileName: string;
  fileId: number;
  position: [number, number, number];
  scale: [number, number, number];
  onChange: ({ fileName, position, scale, fileId }) => void;
}

interface CustomInputProps {
  value?: [number, number, number];
  onChange?: (value: [number, number, number]) => void;
}

const PositionInput = ({ value, onChange }: CustomInputProps) => {
  const [position, setPosition] = React.useState(value || [0, 0, 0]);
  const handleChange = (idx, v) => {
    const newPosition = position.slice();
    newPosition[idx] = v;
    setPosition(newPosition);
    onChange(newPosition as [number, number, number]);
  };
  return (
    <Row gutter={10} style={{ width: "100%" }}>
      <Col span={8}>
        <Row align="middle">
          <Col span={4}>X:</Col>
          <Col span={20}>
            <InputNumber style={{ width: "100%" }} value={position[0]} onChange={(v) => handleChange(0, v)} />
          </Col>
        </Row>
      </Col>
      <Col span={8}>
        <Row align="middle">
          <Col span={4}>Y:</Col>
          <Col span={20}>
            <InputNumber style={{ width: "100%" }} value={position[1]} onChange={(v) => handleChange(1, v)} />
          </Col>
        </Row>
      </Col>
      <Col span={8}>
        <Row align="middle">
          <Col span={4}>Z:</Col>
          <Col span={20}>
            <InputNumber style={{ width: "100%" }} value={position[2]} onChange={(v) => handleChange(2, v)} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
const ScaleInput = ({ onChange, value }: CustomInputProps) => {
  const defaultValue = React.useMemo(() => (value?.[0] ? value[0] * 100 : 100), []);
  const [localScale, setLocalScale] = React.useState(defaultValue);
  const handleChange = (v) => {
    setLocalScale(v);
    const scale = v / 100;
    onChange([scale, scale, scale]);
  };
  return (
    <InputNumber
      min={0}
      formatter={(v) => `${v}%`}
      parser={(v) => +v.toString().replace("%", "")}
      value={localScale}
      onChange={handleChange}
      style={{ width: "100%" }}
    />
  );
};

export const ModelPositionModal = ({
  fileName,
  fileId,
  onChange,
  position,
  scale,
  hideModal,
}: ModelPositionModalProps) => {
  const { t } = useTranslation([I18N_NS.PROJECT, I18N_NS.XKT_VIEWER]);

  const handleFinish = (values) => {
    onChange({ fileName, position: values.position, scale: values.scale, fileId });
    hideModal();
  };

  return (
    <Form
      onFinish={handleFinish}
      initialValues={{ scale: scale?.slice(), position: position?.slice() }}
      layout="vertical"
      requiredMark={false}
    >
      <Row gutter={10}>
        <Col span={18}>
          <Form.Item
            label={t("Position")}
            name="position"
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <PositionInput />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t("Scale")} name="scale" rules={[{ required: true, message: t("This field is required") }]}>
            <ScaleInput />
          </Form.Item>
        </Col>
      </Row>
      <div className="text-right">
        <Button type="primary" htmlType="submit">
          {t("Submit")}
        </Button>
      </div>
    </Form>
  );
};
